import React from "react";
import { FormattedMessage, injectIntl, type IntlShape } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";
import type { RouteComponentProps } from "react-router-dom";

import { clearExtraServices, getExtraServices } from "../../actions/extra_services";
import { getPatientId } from "../../actions/get_patient_id";
import { ARCHES } from "../../common/constants";
import { getLastCorrection } from "../../common/patient";
import { setDocumentTitle } from "../../hooks/use-document-title";
import type { TService } from "../../reducers/dashboard";
import type { RootState } from "../../store";
import FormatDate from "../common/FormatDate";
import Loader from "../common/loadingInProgress";
import { Portlet, PortletTitle } from "../ui/portlet";

const ELASTICS_ID_TO_TRANSLATION = {
  2: "ER_R_II_CLASS",
  3: "ER_R_III_CLASS",
};

const mapStateToProps = (state: RootState) => {
  return {
    services: state.services,
    patient: state.patient,
  };
};

const mapDispatchToProps = {
  getExtraServices,
  clearExtraServices,
  getPatient: getPatientId,
};

type AdditionalAlignersTableProps = PropsFromRedux & { intl: IntlShape } & RouteComponentProps<{
    patient_id: string;
  }>;

class AdditionalAlignersTable extends React.Component<AdditionalAlignersTableProps> {
  componentDidMount() {
    window.scrollTo(0, 0);
    const { patient_id } = this.props.match.params;
    this.props.getPatient(patient_id);
    this.props.clearExtraServices();
    this.props.getExtraServices(patient_id);
    setDocumentTitle(
      this.props.intl.formatMessage({ id: "pat.additional.aligners.page.header" }) +
        " " +
        patient_id,
    );
  }

  renderElastics(service: TService) {
    if (service && service.option) {
      const { elastics_left, elastics_right } = service.option;
      if (elastics_left !== null && elastics_right !== null) {
        return (
          <div>
            <FormattedMessage id="ELASTICS_RIGHT" />
            &nbsp;
            <FormattedMessage id={ELASTICS_ID_TO_TRANSLATION[elastics_right]} />
            <br />
            <FormattedMessage id="ELASTICS_LEFT" />
            &nbsp;
            <FormattedMessage id={ELASTICS_ID_TO_TRANSLATION[elastics_left]} />
          </div>
        );
      } else if (elastics_left !== null) {
        return (
          <div>
            <FormattedMessage id="ELASTICS_LEFT" />
            &nbsp;
            <FormattedMessage id={ELASTICS_ID_TO_TRANSLATION[elastics_left]} />
          </div>
        );
      } else if (elastics_right !== null) {
        return (
          <div>
            <FormattedMessage id="ELASTICS_RIGHT" />
            &nbsp;
            <FormattedMessage id={ELASTICS_ID_TO_TRANSLATION[elastics_right]} />
          </div>
        );
      }
    }
    return null;
  }

  render() {
    const patientIsLoading = this.props.patient.patient_id == undefined;

    if (patientIsLoading) {
      return (
        <div className="page-content-wrapper">
          <div className="page-content" style={{ minHeight: 584 }}>
            <Loader />
          </div>
        </div>
      );
    }

    const canOrderAdditionalAligners = getLastCorrection(this.props.patient)?.order_options
      ?.can_order_additional_aligners;

    if (!canOrderAdditionalAligners) {
      return null;
    }

    return (
      <div className="page-content-wrapper">
        <div className="page-content" id="additional-aligners-table">
          <Portlet as="main">
            <PortletTitle iconClassName="icon-users" id="patients-table-block-title">
              <FormattedMessage id="pat.additional.aligners.page.header" />
            </PortletTitle>

            <div className="portlet-body">
              <div id="additional-aligners_table_wrapper" className="dataTables_wrapper no-footer">
                <div className="table-responsive">
                  <table
                    className="table table-bordered table-hover dataTable no-footer"
                    id="additional_aligners_table"
                    role="grid"
                  >
                    <thead id="additional_aligners-head">
                      <tr role="row">
                        <th>
                          <FormattedMessage id="th.stage" />
                        </th>
                        <th>
                          <FormattedMessage id="th.option" />
                        </th>
                        <th>
                          <FormattedMessage id="AMOUNT" />
                        </th>
                        <th>
                          <FormattedMessage id="HEADER_ELASTICS" />
                        </th>
                        <th>
                          <FormattedMessage id="th.status" />
                        </th>
                        <th>
                          <FormattedMessage id="pat_table.received" />
                        </th>
                        <th>
                          <FormattedMessage id="th.timestamp" />
                        </th>
                      </tr>
                    </thead>

                    <tbody id="additional-aligners-body">
                      {Array.isArray(this.props.services)
                        ? this.props.services
                            .filter((service) => service.tag == "ADDITIONAL_ALIGNERS")
                            .map((service, index) => {
                              return (
                                <tr key={`service${index}`}>
                                  <td>{service.option.stage}</td>
                                  <td>
                                    <FormattedMessage id={ARCHES[service.option.treat_arch_id]} />
                                  </td>
                                  <td>{service.option.amount}</td>
                                  <td>{this.renderElastics(service)}</td>
                                  <td>
                                    <FormattedMessage id={service.option.status} />
                                  </td>
                                  <td>
                                    <FormattedMessage
                                      id={service.paid ? "bool.true" : "bool.false"}
                                    />
                                  </td>
                                  <td>
                                    <FormatDate value={service.timestamp} date />
                                  </td>
                                </tr>
                              );
                            })
                        : null}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Portlet>
        </div>
      </div>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(AdditionalAlignersTable));
